import { InputStructureProps } from "../../../Reusables/components/CygnusFormBuilder/CygnusFormBuilder.utils";
import { makeCygnusApiCall } from "../../../api/api";

/**
 * Fetch all grouped templates based on the selected purpose.
 *
 * @returns {Promise<any>} A map of grouped templates.
 */
export const fetchGroupedTemplates = async (): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: "/forms/templates/grouped/",
    show_message: true,
    customized_success_message: "Fetched grouped templates.",
    customized_failure_message:
      "Unable to fetch grouped templates. Please try again.",
    customized_error_message:
      "Error occurred while fetching grouped templates.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    if (response.success) {
      return response.data;
    }
  }
};

/**
 * Fetch pre-defined firm template fields used to power Cygnus form dropdowns.
 *
 * @returns {Promise<any>} List of selectable field options.
 */
export const fetchFirmTemplateFields = async (): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: "/engagement-letter/firm_template_fields/",
    show_message: true,
    customized_success_message: "Fetched firm template fields.",
    customized_failure_message: "Unable to fetch firm template fields.",
    customized_error_message:
      "Error occurred while fetching firm template fields.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return response.data;
  }
};

/**
 * Save a new form entry (Draft or Final).
 *
 * @param payload - The full form configuration payload.
 * @returns {Promise<any>} Save result.
 */
export const saveFormData = async (
  payload: {
    FirmFormId?: string;
    FormName: string;
    FormDescription: string;
    InputStructure: InputStructureProps;
    FormTypeCode: string;
    LineOfServiceCode: string;
    Status: string;
  },
  update: boolean
): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: update ? "PUT" : "POST",
    url: "/forms/",
    payload,
    show_message: true,
    customized_success_message: "Form saved successfully.",
    customized_failure_message: "Failed to save form. Please try again.",
    customized_error_message: "Error occurred while saving form.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return response.data;
  }
};

/**
 * Update an existing form using PUT.
 *
 * @param payload - The update configuration payload.
 * @returns {Promise<any>} Update result.
 */
export const updateFormData = async (payload: {
  FirmFormId: string;
  FormName: string;
  FormDescription: string;
  FormPurpose: string;
  InputStructure: InputStructureProps;
  PreviewStructure: any[];
  FormTypeCode: string;
  Type: string;
  Status: string;
}): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "PUT",
    url: "/forms/",
    payload,
    show_message: true,
    customized_success_message: "Form updated successfully.",
    customized_failure_message: "Failed to update form.",
    customized_error_message: "Error occurred while updating form.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return response.data;
  }
};

/**
 * Fetch form purposes (reference data with domain = FormType).
 *
 * @returns {Promise<any>} List of form purpose types.
 */
export const fetchFormPurposeTypes = async (): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/reference_data?ReferenceDataDomain=FormType`,
    show_message: true,
    customized_success_message: "Fetched Form Purpose data.",
    customized_failure_message:
      "Unable to fetch your form data, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occurred while fetching your form data, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return response.data;
  }
};

/**
 * Fetch client types (reference data with domain = ClientType).
 *
 * @returns {Promise<any>} List of client type values.
 */
export const fetchClientTypes = async (): Promise<any> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/reference_data?ReferenceDataDomain=ClientType`,
    show_message: true,
    customized_success_message: "Fetched Form client type data.",
    customized_failure_message:
      "Unable to fetch your client type data, please try again. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message:
      "Error occurred while fetching your client type data, please contact support and attach a screenshot.",
    isblob: false,
    forMDM: true,
  });

  if (response.success) {
    return response.data;
  }
};

/**
 * Fetch purpose/Line of Service options directly
 */
export const fetchPurposeOptions = async (): Promise<any[]> => {
  try {
    const token = localStorage.getItem("token");
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URI}/mdm/purpose-fields/`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch purpose options");
    }

    const data = await response.json();
    console.log("fetchPurposeOptions response:", data);
    
    // Map the response to the expected format
    // Adjust based on your actual API response structure
    return (data?.data || []).map((item: any) => ({
      code: item.code || item.FieldCode,
      label: item.label || item.FieldName,
      value: item.value || item.code?.replace('for_', '') || item.FieldCode?.replace('for_', ''),
    }));
  } catch (error) {
    console.error("Error fetching purpose options:", error);
    return [];
  }
};